export function getFormattedTimeElapsed(startDate: Date): string {
  const now: Date = new Date()
  const elapsedMilliseconds: number = now.getTime() - startDate.getTime()

  const seconds: number = Math.floor(elapsedMilliseconds / 1000)
  const minutes: number = Math.floor(seconds / 60)
  const hours: number = Math.floor(minutes / 60)
  const days: number = Math.floor(hours / 24)

  if (days > 0) {
    return days === 1 ? '1 day ago' : `${days} days ago`
  }
  if (hours > 0) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`
  }
  if (minutes > 0) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
  }
  return seconds === 1 ? '1 second ago' : `${seconds} seconds ago`
}

export function getFormattedBytes(bytes: number | null): string {
  if (!bytes) return '0 bytes'

  const k = 1024
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const formattedNumber = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    bytes / k ** i
  )

  return `${formattedNumber} ${sizes[i]}`
}

export function getFormattedDuration(
  startedAt?: string | Date,
  finishedAt?: string | Date,
  durationInMillis?: number
): string | null {
  if (!durationInMillis) {
    if (!startedAt) return null
    const startTime = typeof startedAt === 'string' ? new Date(startedAt).getTime() : startedAt.getTime()
    const currentTime = finishedAt
      ? typeof finishedAt === 'string'
        ? new Date(finishedAt).getTime()
        : finishedAt.getTime()
      : new Date().getTime()
    durationInMillis = currentTime - startTime
  }

  if (durationInMillis < 1000) {
    return `${durationInMillis}ms`
  }
  if (durationInMillis < 2000) {
    return `1s`
  }
  if (durationInMillis < 60000) {
    return `${Math.round(durationInMillis / 1000)}s`
  }
  if (durationInMillis < 120000) {
    return `1m`
  }
  return `${(durationInMillis / 60000).toFixed(1)}m`
}

export function getFormattedNumberToMillions(number: number | null) {
  return !number
    ? '0.0 M'
    : number >= 1_000_000
      ? `${(number / 1_000_000).toFixed(1)} M`
      : `${(number / 1_000_000).toFixed(3)} M`
}

export function getFormattedNumber(number: number) {
  if (number === 0) return '0'

  const thresholds = [
    { value: 1_000_000_000_000, symbol: 'T' },
    { value: 1_000_000_000, symbol: 'B' },
    { value: 1_000_000, symbol: 'M' },
    { value: 1_000, symbol: 'K' },
  ]

  for (let i = 0; i < thresholds.length; i++) {
    if (number >= thresholds[i].value) {
      return (number / thresholds[i].value).toFixed(2) + thresholds[i].symbol
    }
  }

  return number.toString()
}

export const formatDate = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-us', {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  })
}

export const formatTime = (date: string | Date, includeSeconds: boolean = false): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleTimeString('en-us', {
    hour: '2-digit',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
    hour12: true,
  })
}
